import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, TextField, FormHelperText, Typography } from '@mui/material';

import useIdentifyBlackListedWords from '../../hooks/useIdentifyBlackListedWords';
import {
  FORTA_BLACK,
  FORTA_ERROR,
  FORTA_ORANGE,
  FORTA_SUCCESS,
  FORTA_WHITE,
} from '../../utils/colors';
import { getThemeColor } from '../../utils/common';
import { Themes } from '../../constants';

import type { StandardTextFieldProps, FormHelperTextProps } from '@mui/material';
import type { ChangeEvent } from 'react';

// This component is an extention of FHTextarea which can identify use of blacklisted words.
interface TextareaProps extends StandardTextFieldProps {
  colorTheme?: Themes;
  coloredBackground?: boolean;
  expandable?: boolean;
  error?: boolean;
  warning?: boolean;
  fullHeight?: boolean;
  onEdit?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onCloseExpandDialog?: () => void;
}

interface FHSmartTextareaProps extends TextareaProps {
  minLength?: number;
  heightlightColour?: string;
  hightlightOnDetection?: boolean;
  blackListedWordsPhrases?: string[];
  id: string; // id is required and should be unique in the DOM
}

const StyledTextarea = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== 'error' &&
    prop !== 'warning' &&
    prop !== 'colorTheme' &&
    prop !== 'coloredBackground' &&
    prop !== 'fullHeight',
})<TextareaProps>(({ colorTheme, coloredBackground, error, warning, fullHeight }: any) => ({
  backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
  borderRadius: '16px',
  border: error
    ? `2px solid ${FORTA_ERROR}`
    : warning
      ? `2px solid ${FORTA_ORANGE}`
      : `2px solid ${coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE}`,
  '&:focus-within': {
    border: error
      ? `2px solid ${FORTA_ERROR}`
      : warning
        ? `2px solid ${FORTA_ORANGE}`
        : `2px solid ${FORTA_SUCCESS}`,
  },
  padding: '0.625rem',
  boxShadow: fullHeight
    ? '0px 4px 4px rgba(0, 0, 0, 0.25)'
    : `0px 4px 4px ${getThemeColor(colorTheme).light2}`,
  '& .MuiInputBase-root': {
    color: FORTA_BLACK,
    padding: 0,
    fontSize: 16,
    height: fullHeight ? '100%' : 'initial',

    '& .MuiInputBase-inputMultiline': {
      height: fullHeight ? '100%!important' : 'initial',
    },

    '& ::-webkit-scrollbar': {
      width: '12px',
    },

    '& ::-webkit-scrollbar-thumb': {
      visibility: fullHeight ? 'visible' : 'hidden',
    },

    '& ::-webkit-scrollbar-track': {
      visibility: fullHeight ? 'visible' : 'hidden',
    },

    '&:before': {
      display: 'none',
    },
    '&:after': {
      display: 'none',
    },

    '& .MuiInput-input.Mui-disabled': {
      WebkitTextFillColor: FORTA_BLACK,
    },

    '& .MuiInput-input': {
      overflowX: 'hidden',
      overflowY: 'auto',
    },
  },
  '& .MuiFormHelperText-root': {
    position: 'absolute',
    bottom: 0,
    backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
    color: getThemeColor(colorTheme).primary,
    fontWeight: 'bold',
    paddingLeft: '2.5em',
    marginBottom: '0.25em',
    fontSize: '1em',
    borderRadius: 10,
  },
}));

const StyledFormHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) =>
    prop !== 'error' &&
    prop !== 'colorTheme' &&
    prop !== 'coloredBackground' &&
    prop !== 'fullHeight',
})<FormHelperTextProps>(({ colorTheme, error }: any) => ({
  color:
    error === null || error === undefined
      ? `${getThemeColor(colorTheme).primary}`
      : error === false
        ? `${FORTA_SUCCESS}`
        : `${FORTA_ERROR}`,
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '21px',
}));

const FHSmartTextarea = (props: FHSmartTextareaProps) => {
  const {
    id,
    fullWidth = true,
    fullHeight = false,
    colorTheme = Themes.Primary,
    coloredBackground = false,
    expandable: _expandable = false,
    error = false,
    warning: _warning = false,
    placeholder,
    value = '',
    minLength: _minLength = 0,
    disabled = false,
    helperText,
    hightlightOnDetection = false,
    blackListedWordsPhrases = [],
    heightlightColour = FORTA_ORANGE,
    onEdit,
    onCloseExpandDialog: _onCloseExpandDialog,
    ...textAreaProps
  } = props;

  const textareaRef = useRef<HTMLDivElement>(null);
  const highlightDivRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState<string>(String(value ?? ''));

  const [doBlacklistedWordsExist, setDoBlacklistedWordsExist] = useState<boolean>(false);

  const blackListed = useIdentifyBlackListedWords(String(value ?? ''), blackListedWordsPhrases);

  useEffect(() => {
    setDoBlacklistedWordsExist(blackListed?.length > 0);
    handleInput(text);
  }, [blackListed]);

  function handleInput(value: string) {
    const highlightedText = applyHighlights(value);
    setText(value);
    const ele = document.getElementById(`smartTextarea-highlight-${id}`);
    if (ele) {
      ele.innerHTML = highlightedText;
    }
  }

  function applyHighlights(text: string) {
    const regex = new RegExp(`\\b(${blackListed.join('|')})\\b`, 'gi');
    return text.replace(regex, (match) => `<span>${match}</span>`);
  }

  const handleScroll = (e: any) => {
    if (textareaRef.current && highlightDivRef.current) {
      highlightDivRef.current.scrollTop = e.target.scrollTop;
      highlightDivRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  return (
    <Box>
      {doBlacklistedWordsExist && (
        <Typography sx={{ color: FORTA_ORANGE, fontWeight: 700, textAlign: 'left', mb: 0.25 }}>
          Please refrain from using the words highlighted in the text input.
        </Typography>
      )}
      <Box
        sx={{
          position: 'relative',
          flexGrow: 1,
          minHeight: 194,
          p: 0,
          borderRadius: '16px',
          backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
        }}
      >
        <Box
          ref={highlightDivRef}
          id={`smartTextarea-highlight-${id}`}
          sx={{
            height: textareaRef.current
              ? textareaRef.current.getBoundingClientRect().height
              : 'calc(100% - 10px);',
            width: '100%',
            borderRadius: '16px',
            fontFamily: 'DMSansRegular',
            fontStyle: 'normal',
            fontSize: '1rem',
            letterSpacing: 'normal',
            whiteSpace: 'pre-wrap',
            lineHeight: '24px',
            padding: '0.625rem 0.75rem 0.625rem 0.75rem',
            textAlign: 'start',
            wordWrap: 'break-word',
            color: 'transparent',
            overflowX: 'hidden',
            overflowY: 'auto',
            clipPath: 'inset(0.65rem)',
            position: 'absolute',
            top: 0,
            left: 0,
            backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
            '&::-webkit-scrollbar-thumb': {
              visibility: fullHeight ? 'visible' : 'hidden',
            },
            '&::-webkit-scrollbar-track': {
              visibility: fullHeight ? 'visible' : 'hidden',
            },
            '& span': {
              color: 'transparent',
              margin: 0,
              padding: 0,
              backgroundColor: heightlightColour,
            },
          }}
        />
        <StyledTextarea
          {...textAreaProps}
          ref={textareaRef}
          sx={{
            font: 'inherit',
            letterSpacing: 'normal',
            color: FORTA_BLACK,
            backgroundColor: 'transparent',
            wordWrap: 'break-word',
            position: 'absolute',
            top: 0,
            left: 0,
            overflow: 'auto',
            '& textarea': {
              lineHeight: '24px!important',
            },
          }}
          onScrollCapture={(e) => handleScroll(e)}
          placeholder={placeholder}
          disabled={disabled}
          variant="standard"
          multiline
          fullWidth={fullWidth}
          fullHeight={fullHeight}
          minRows={7} // Highlight words within text area is not nativly supported by HTML, it needs a fixed height to work. TODO - Need to make the box height dynamic if need arises.
          maxRows={7}
          colorTheme={colorTheme}
          coloredBackground={coloredBackground}
          value={value}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
            if (onEdit) {
              onEdit(e);
            }
            handleInput(e.target.value);
          }}
          error={error}
          warning={hightlightOnDetection && blackListed.length > 0}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
            position: 'absolute',
            bottom: -20,
            right: 0,
          }}
        >
          <StyledFormHelperText error={error}>{helperText}</StyledFormHelperText>
        </Box>
      </Box>
    </Box>
  );
};

export default FHSmartTextarea;
